import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface DescriptionItem {
  label: ReactNode;
  value: ReactNode;
}

/**
 * Read-only label/value pairs — profile details, record metadata, summaries.
 *
 * Uses a real `<dl>`. The half-dozen hand-rolled versions of this were `div`s
 * with two `span`s, which reads to a screen reader as an undifferentiated run
 * of text with no association between a field and its value.
 *
 * `layout="rows"` gives label-left/value-right rows (good in a narrow card);
 * `layout="stack"` puts the label above the value in a responsive grid (good
 * for wider metadata blocks).
 */
export function DescriptionList({
  items,
  layout = 'rows',
  columns = 2,
  className,
}: {
  items: DescriptionItem[];
  layout?: 'rows' | 'stack';
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  if (layout === 'stack') {
    return (
      <dl
        className={cn(
          'grid gap-x-6 gap-y-3',
          columns === 1 ? 'grid-cols-1' : columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3',
          className,
        )}
      >
        {items.map((item, i) => (
          <div key={i} className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{item.label}</dt>
            <dd className="mt-0.5 truncate text-sm">{item.value ?? '—'}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className={cn('text-sm', className)}>
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-4 border-b border-border/60 py-2 first:pt-0 last:border-0 last:pb-0"
        >
          <dt className="shrink-0 text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 text-right font-medium">{item.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
